import Phaser from 'phaser';
import type { GameConfig, GameFeatures } from './config/types';
import { DEFAULT_CONFIG, DEFAULT_FEATURES } from './config/defaults';

/**
 * Interface for global signals (lifecycle events)
 * This matches the GlobalSignals interface from @repo/plugin-signals
 */
interface GlobalSignals {
  lifecycle: {
    gamePause: {
      emit: (data: { timestamp: number; sceneKey: string }) => void;
    };
  };
}

/**
 * Type for a Phaser scene plugin class constructor.
 * Used for dependency injection of plugins.
 */
type ScenePluginClass = new (
  scene: Phaser.Scene,
  pluginManager: Phaser.Plugins.PluginManager,
  pluginKey: string
) => Phaser.Plugins.ScenePlugin;

/**
 * Dependencies interface for optional feature imports
 */
export interface GameDependencies {
  SplashScene?: typeof Phaser.Scene;
  PreloaderScene?: typeof Phaser.Scene;
  HighscoreScene?: typeof Phaser.Scene;
  RaffleScene?: typeof Phaser.Scene;
  StatePlugin?: ScenePluginClass;
  SignalsPlugin?: ScenePluginClass;
  HighscorePlugin?: ScenePluginClass;
  TimeTrackerPlugin?: ScenePluginClass;
  ApiPlugin?: ScenePluginClass;
  RafflePlugin?: ScenePluginClass;
  AnalyticsPlugin?: ScenePluginClass;
}

/**
 * GameWrapper - Core wrapper class for Phaser games
 *
 * Handles game initialization, scene composition, and plugin registration
 * based on enabled features.
 */
export class GameWrapper {
  private game: Phaser.Game | null = null;
  private config: GameConfig;
  private features: GameFeatures;
  private dependencies: GameDependencies;
  private visibilityHandler?: () => void;
  private blurHandler?: () => void;

  /**
   * @param gameScenes - Array of game-specific scene classes
   * @param config - Partial game configuration (merged with defaults)
   * @param features - Feature flags to enable optional functionality
   * @param dependencies - Optional imports for features (auto-imported if not provided)
   */
  constructor(
    private gameScenes: Array<typeof Phaser.Scene>,
    config: Partial<GameConfig> = {},
    features: GameFeatures = {},
    dependencies: GameDependencies = {}
  ) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.features = { ...DEFAULT_FEATURES, ...features };
    this.dependencies = dependencies;
  }

  /**
   * Initialize and start the Phaser game
   */
  initialize(): Phaser.Game {
    const phaserConfig: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      width: this.config.width,
      height: this.config.height,
      backgroundColor: this.config.backgroundColor,
      parent: this.config.parent,
      scale: this.buildScaleConfig(),
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { y: 0, x: 0 },
          debug: this.config.debug,
        },
      },
      scene: this.buildSceneList(),
      plugins: this.buildPluginConfig(),
    };

    this.game = new Phaser.Game(phaserConfig);

    // Set up focus detection if enabled
    if (this.features.pauseOnLostFocus) {
      this.setupFocusDetection();
    }

    return this.game;
  }

  /**
   * Set up focus/visibility detection to pause the game when focus is lost.
   * Uses two mechanisms:
   * 1. Page Visibility API (document.visibilitychange) - detects tab switching
   * 2. Window blur event - detects when another application takes focus
   *
   * Only emits pause signal - user must manually resume via the pause modal.
   */
  private setupFocusDetection(): void {
    if (!this.game) return;

    // Get signals from game registry (will be available after first scene boots)
    const getSignals = (): GlobalSignals | undefined => {
      return this.game?.registry.get('__signals_global__') as GlobalSignals | undefined;
    };

    // Helper to emit pause signal
    const emitPause = (source: string) => {
      const signals = getSignals();
      signals?.lifecycle.gamePause.emit({
        timestamp: Date.now(),
        sceneKey: source,
      });
    };

    // Handler for Page Visibility API (tab switching)
    this.visibilityHandler = () => {
      if (document.hidden) {
        emitPause('visibility-change');
      }
    };

    // Handler for window blur (another application takes focus)
    this.blurHandler = () => {
      // Only pause on blur if the document is not already hidden
      // (to avoid double-pausing when switching tabs)
      if (!document.hidden) {
        emitPause('window-blur');
      }
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
    window.addEventListener('blur', this.blurHandler);
  }

  /**
   * Remove focus detection listeners
   */
  private removeFocusDetection(): void {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = undefined;
    }
    if (this.blurHandler) {
      window.removeEventListener('blur', this.blurHandler);
      this.blurHandler = undefined;
    }
  }

  /**
   * Build scale configuration for responsive canvas
   */
  private buildScaleConfig(): Phaser.Types.Core.ScaleConfig {
    const scaleConfig: Phaser.Types.Core.ScaleConfig = {
      width: this.config.width,
      height: this.config.height,
      parent: this.config.parent,
    };

    // Set scale mode
    switch (this.config.scaleMode) {
      case 'fit':
        scaleConfig.mode = Phaser.Scale.FIT;
        break;
      case 'fill':
        scaleConfig.mode = Phaser.Scale.ENVELOP;
        break;
      case 'smooth':
        scaleConfig.mode = Phaser.Scale.FIT;
        break;
      case 'none':
      default:
        scaleConfig.mode = Phaser.Scale.NONE;
        break;
    }

    // Set auto center
    if (this.config.autoCenter !== false) {
      scaleConfig.autoCenter = Phaser.Scale.CENTER_BOTH;
    }

    // Set min/max dimensions if scaling is enabled
    if (this.config.scaleMode && this.config.scaleMode !== 'none') {
      scaleConfig.min = {
        width: this.config.minWidth ?? 320,
        height: this.config.minHeight ?? 480,
      };
      scaleConfig.max = {
        width: this.config.maxWidth ?? this.config.width * 2,
        height: this.config.maxHeight ?? this.config.height * 2,
      };
    }

    return scaleConfig;
  }

  /**
   * Build the scene list based on enabled features
   */
  private buildSceneList(): Array<typeof Phaser.Scene> {
    const scenes: Array<typeof Phaser.Scene> = [];

    // Add splash scene if enabled
    if (this.features.splash && this.dependencies.SplashScene) {
      scenes.push(this.dependencies.SplashScene);
    }

    // Add preloader scene if enabled
    if (this.features.preloader && this.dependencies.PreloaderScene) {
      scenes.push(this.dependencies.PreloaderScene);
    }

    // Add game-specific scenes
    scenes.push(...this.gameScenes);

    // Add highscore scene if enabled
    if (this.features.highscore && this.dependencies.HighscoreScene) {
      scenes.push(this.dependencies.HighscoreScene);
    }

    // Add raffle scene if enabled
    if (this.features.raffle && this.dependencies.RaffleScene) {
      scenes.push(this.dependencies.RaffleScene);
    }

    return scenes;
  }

  /**
   * Build plugin configuration based on enabled features
   */
  private buildPluginConfig(): Phaser.Types.Core.PluginObject {
    const plugins: Phaser.Types.Core.PluginObject = {
      scene: [],
    };

    // Always include state management plugin
    if (this.dependencies.StatePlugin) {
      plugins.scene!.push({
        key: 'StatePlugin',
        plugin: this.dependencies.StatePlugin,
        mapping: 'state',
      });
    }

    // Always include signals plugin
    if (this.dependencies.SignalsPlugin) {
      plugins.scene!.push({
        key: 'SignalsPlugin',
        plugin: this.dependencies.SignalsPlugin,
        mapping: 'signals',
      });
    }

    // Add API plugin if enabled
    if (this.features.api && this.dependencies.ApiPlugin) {
      plugins.scene!.push({
        key: 'ApiPlugin',
        plugin: this.dependencies.ApiPlugin,
        mapping: 'api',
      });
    }

    // Add highscore plugin if enabled
    if (this.features.highscore && this.dependencies.HighscorePlugin) {
      plugins.scene!.push({
        key: 'HighscorePlugin',
        plugin: this.dependencies.HighscorePlugin,
        mapping: 'highscore',
      });
    }

    // Add timetracker plugin if provided
    if (this.dependencies.TimeTrackerPlugin) {
      plugins.scene!.push({
        key: 'TimeTrackerPlugin',
        plugin: this.dependencies.TimeTrackerPlugin,
        mapping: 'timetracker',
      });
    }

    // Add raffle plugin if enabled
    if (this.features.raffle && this.dependencies.RafflePlugin) {
      plugins.scene!.push({
        key: 'RafflePlugin',
        plugin: this.dependencies.RafflePlugin,
        mapping: 'raffle',
      });
    }

    // Add analytics plugin if enabled
    if (this.features.analytics && this.dependencies.AnalyticsPlugin) {
      plugins.scene!.push({
        key: 'AnalyticsPlugin',
        plugin: this.dependencies.AnalyticsPlugin,
        mapping: 'analytics',
      });
    }

    return plugins;
  }

  /**
   * Destroy the game instance
   */
  destroy(): void {
    this.removeFocusDetection();
    if (this.game) {
      this.game.destroy(true);
      this.game = null;
    }
  }

  /**
   * Get the game instance
   */
  getGame(): Phaser.Game | null {
    return this.game;
  }
}
